import type { Database } from "@/lib/database.types";

export const pqrCategories = [
  "bug_report",
  "feature_request",
  "billing_question",
  "account_issue",
  "content_question",
  "complaint",
  "other",
] as const;

export const pqrStatuses = [
  "open",
  "in_review",
  "resolved",
  "closed",
] as const;

export const pqrMessageSenderRoles = [
  "admin",
] as const;

export const pqrAttachmentMimeTypes = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
] as const;

export const pqrMaxAttachments = 3;
export const pqrMaxAttachmentBytes = 5_242_880;
export const pqrPageSize = 20;
export const pqrAttachmentSignedUrlExpirySeconds = 60 * 15;
export const pqrTicketRefPattern = /^LSC-\d{8}-\d{4,}$/;

export type PqrCategory = (typeof pqrCategories)[number];
export type PqrStatus = (typeof pqrStatuses)[number];
export type PqrMessageSenderRole = (typeof pqrMessageSenderRoles)[number];
export type PqrAttachmentMimeType = (typeof pqrAttachmentMimeTypes)[number];

export type PqrRecord = Database["public"]["Tables"]["pqr_submissions"]["Row"];
export type PqrInsert = Database["public"]["Tables"]["pqr_submissions"]["Insert"];
export type PqrReplyRecord = Database["public"]["Tables"]["pqr_thread_messages"]["Row"];

export type PqrFunctionErrorCode =
  | "PQR_UNAUTHORIZED"
  | "PQR_INVALID_TICKET_REF"
  | "PQR_TOO_MANY_ATTACHMENTS"
  | "PQR_INVALID_ATTACHMENT_TYPE"
  | "PQR_ATTACHMENT_TOO_LARGE"
  | "PQR_INVALID_CATEGORY"
  | "PQR_INVALID_NAME"
  | "PQR_INVALID_EMAIL"
  | "PQR_INVALID_SUBJECT"
  | "PQR_INVALID_MESSAGE"
  | "PQR_INVALID_REPLY_MESSAGE"
  | "PQR_REPLY_UNAVAILABLE"
  | "PQR_STORAGE_UPLOAD_FAILED"
  | "PQR_INSERT_FAILED";

export interface PqrSubmissionDraft {
  category: PqrCategory;
  email: string;
  message: string;
  name: string;
  subject: string;
}

export interface PqrSubmissionResult {
  createdAt: string;
  status: PqrStatus;
  ticketRef: string;
}

export interface PqrAttachmentLink {
  fileName: string;
  path: string;
  signedUrl: string;
}

export interface PqrHistoryItem {
  attachmentCount: number;
  category: PqrCategory;
  createdAt: string;
  id: string;
  status: PqrStatus;
  subject: string;
  ticketRef: string;
}

export interface PqrRequestDetails {
  attachmentPaths: string[];
  category: PqrCategory;
  createdAt: string;
  email: string;
  id: string;
  message: string;
  name: string;
  status: PqrStatus;
  statusHistory: PqrStatusHistoryEntry[];
  subject: string;
  ticketRef: string;
  userId: string | null;
}

export interface PqrStatusHistoryEntry {
  changedAt: string;
  changedBy: string | null;
  note: string | null;
  status: PqrStatus;
}

export interface PqrThreadMessage {
  body: string;
  createdAt: string;
  id: string;
  senderRole: PqrMessageSenderRole;
  senderUserId: string | null;
}

export interface PqrHistoryPage {
  items: PqrHistoryItem[];
  page: number;
  pageCount: number;
  totalCount: number;
}

export interface PqrAdminListItem {
  category: PqrCategory;
  createdAt: string;
  email: string;
  id: string;
  name: string;
  status: PqrStatus;
  subject: string;
  ticketRef: string;
  userId: string | null;
}

export interface PqrAdminPage {
  items: PqrAdminListItem[];
  page: number;
  pageCount: number;
  totalCount: number;
}
